/**
 * SeedAuditEntry
 *
 * Renders a single TEMPLATE_SEED or INDUSTRY_PACK_SEED audit entry. Default
 * view is a compact one-liner with action-label, performer, performedAt, and
 * the source/dryRun chips. A "▾ Vis detaljer" toggle reveals a per-entity-
 * type table listing every created/updated/skipped item's natural key.
 *
 * The default-collapsed shape keeps the audit list scannable when many seed
 * events stack up; expanding is opt-in for the operator who actually needs
 * the forensic listing.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import type { PlatformAuditLog } from '../../types';
import type { SeedAuditDetails } from '../../services/auditService';

interface SeedAuditEntryProps {
  entry: PlatformAuditLog;
}

/**
 * Norwegian labels for the seed actions surfaced in this section. Other
 * audit actions are rendered with their raw enum value as a fallback —
 * this component only knows how to expand TEMPLATE_SEED + INDUSTRY_PACK_SEED.
 */
const SEED_ACTION_LABELS_NO: Record<string, string> = {
  TEMPLATE_SEED: 'Master template-seeding',
  INDUSTRY_PACK_SEED: 'Industri-pakke installert',
};

/**
 * Human-readable Norwegian labels for the entity-type keys stored on
 * `details.perEntityType`. Falls back to the raw enum value so a forward-
 * compat audit row written by a newer backend still renders cleanly.
 */
const ENTITY_TYPE_LABELS_NO: Record<string, string> = {
  permissions: 'Permissions',
  roles: 'Roller',
  consent_bases: 'Samtykkegrunnlag',
  status_flows: 'Statusflyt',
  sources: 'Kilder',
  checklist_groups: 'Sjekklistegrupper',
  activity_types: 'Aktivitetstyper',
  task_types: 'Oppgavetyper',
  folder_templates: 'Mappemaler',
  categories: 'Kategorier',
  resource_links: 'Ressurslenker',
  sms_templates: 'SMS-maler',
  email_templates: 'E-postmaler',
};

const formatPerformedAt = (iso: string): string => {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, 'dd.MM.yyyy HH:mm');
  } catch {
    return 'Invalid date';
  }
};

const formatPerformer = (entry: PlatformAuditLog): string => {
  const { firstName, lastName, email } = entry.user;
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  return name.length > 0 ? name : email;
};

/**
 * Render a single natural-key map as a comma-separated key=value list. The
 * audit entries store the raw natural-key fields (e.g. `{ name: 'X',
 * assignmentType: 'SINGLE_SALE' }`) so the operator can correlate with the
 * tenant DB when investigating.
 */
const formatNaturalKey = (key: Record<string, unknown>): string => {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(key)) {
    if (v === null || v === undefined) continue;
    parts.push(`${k}: ${String(v)}`);
  }
  return parts.length > 0 ? parts.join(' / ') : '(uten naturlig nøkkel)';
};

export function SeedAuditEntry({ entry }: SeedAuditEntryProps) {
  const [expanded, setExpanded] = useState(false);
  const details = (entry.details as SeedAuditDetails | null) ?? {};
  const actionLabel = SEED_ACTION_LABELS_NO[entry.action] ?? entry.action;
  const isDryRun = details.dryRun === true;
  const perEntityType = details.perEntityType ?? {};
  const entityRows = Object.entries(perEntityType);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: 'wrap', mb: 0.5 }}
          >
            <Typography variant="body2" fontWeight={500}>
              {actionLabel}
            </Typography>
            {details.source && (
              <Chip
                label={details.source}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
            )}
            {isDryRun && (
              <Chip
                label="Forhåndsvisning"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatPerformer(entry)} · {formatPerformedAt(entry.performedAt)}
            {details.environment ? ` · ${details.environment}` : ''}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded((prev) => !prev)}
          startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ minHeight: 36 }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Skjul detaljer' : 'Vis detaljer'}
        >
          {expanded ? 'Skjul detaljer' : 'Vis detaljer'}
        </Button>
      </Box>

      {expanded && entityRows.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Opprettet</TableCell>
                  <TableCell align="right">Oppdatert</TableCell>
                  <TableCell align="right">Hoppet over</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entityRows.map(([entityType, data]) => (
                  <TableRow key={entityType}>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {ENTITY_TYPE_LABELS_NO[entityType] ?? entityType}
                      </Typography>
                      {data.created.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Opprettet:
                          </Typography>
                          {data.created.map((item, idx) => (
                            <Typography
                              key={`c-${idx}`}
                              variant="caption"
                              component="div"
                              color="text.secondary"
                              sx={{ pl: 1 }}
                            >
                              · {formatNaturalKey(item.naturalKey)}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {data.updated.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Oppdatert:
                          </Typography>
                          {data.updated.map((item, idx) => (
                            <Typography
                              key={`u-${idx}`}
                              variant="caption"
                              component="div"
                              color="text.secondary"
                              sx={{ pl: 1 }}
                            >
                              · {formatNaturalKey(item.naturalKey)}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {data.skipped.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Hoppet over:
                          </Typography>
                          {data.skipped.map((item, idx) => (
                            <Typography
                              key={`s-${idx}`}
                              variant="caption"
                              component="div"
                              color="text.secondary"
                              sx={{ pl: 1 }}
                            >
                              · {formatNaturalKey(item.naturalKey)}
                              {item.reason ? ` (${item.reason})` : ''}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {data.created.length}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {data.updated.length}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {data.skipped.length}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {expanded && entityRows.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Ingen detaljer registrert for denne hendelsen.
        </Typography>
      )}
    </Box>
  );
}
